import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';
import DefaultButton from '../../../../../components/Accessibility/DefaultButton';
import TypographyAccessibility from '../../../../../components/Accessibility/TypographyAccessibility';
import { ModalDefault } from '../../../../../components/ModalDefault';
import { TranscricaoItemDetalhe } from '../../../../../interfaces/entities/Transcricao/TranscricaoRevisao';
import { obterDescricaoItem } from '../../../../../services/TranscricaoBeneficiarioService';
import { sxLabel } from '../../../../../assets/styles/CommonSxProps';
import { useAccessibility } from '../../../../../Store/contexts/AccessibilityContext';
import {
  sxActions,
  sxDescricaoTextField,
  sxField,
  sxFormGrid,
  sxError,
  sxFormRow,
  sxModalTitle,
  sxSelect,
  sxTextField,
} from './editarProcedimentoModalSx';
import { normalizeTipoItemCodigo, TIPO_ITEM_OPTIONS, TipoItemCodigo } from '../../utils/tipoItemUtils';

const CODIGO_TUSS_INVALIDO_MESSAGE = 'Código TUSS inválido';

const DESCRICAO_MAX_LENGTH = 500;

export interface EditarProcedimentoFormValues {
  tipoItem: TipoItemCodigo | '';
  codigoTuss: string;
  descricao: string;
  quantidade: string;
}

interface EditarProcedimentoModalProps {
  open: boolean;
  procedimento: TranscricaoItemDetalhe | null;
  onClose: () => void;
  onSave: (idTranscricaoItem: string, values: EditarProcedimentoFormValues) => void;
}

const formatQuantidade = (quantidade: number): string =>
  String(quantidade).padStart(2, '0');

const mapProcedimentoToForm = (
  procedimento: TranscricaoItemDetalhe,
): EditarProcedimentoFormValues => ({
  tipoItem: normalizeTipoItemCodigo(procedimento.tipoProdMed),
  codigoTuss: procedimento.codigo ?? '',
  descricao: procedimento.descricao ?? '',
  quantidade: formatQuantidade(procedimento.quantidade ?? 0),
});

const EditarProcedimentoModal: React.FC<EditarProcedimentoModalProps> = ({
  open,
  procedimento,
  onClose,
  onSave,
}) => {
  const accessibility = useAccessibility();
  const codigoTussOriginalRef = useRef('');
  const formValuesRef = useRef<EditarProcedimentoFormValues>({
    tipoItem: '',
    codigoTuss: '',
    descricao: '',
    quantidade: '',
  });
  const [carregandoDescricao, setCarregandoDescricao] = useState(false);
  const [codigoTussError, setCodigoTussError] = useState('');
  const [formValues, setFormValues] = useState<EditarProcedimentoFormValues>({
    tipoItem: '',
    codigoTuss: '',
    descricao: '',
    quantidade: '',
  });

  useEffect(() => {
    if (!open || !procedimento) return;
    const valores = mapProcedimentoToForm(procedimento);
    setFormValues(valores);
    formValuesRef.current = valores;
    codigoTussOriginalRef.current = valores.codigoTuss;
    setCodigoTussError('');
  }, [open, procedimento]);

  const handleFieldChange = (field: keyof EditarProcedimentoFormValues, value: string) => {
    setFormValues((current) => {
      const updated = { ...current, [field]: value };
      formValuesRef.current = updated;
      return updated;
    });
  };

  const handleCodigoTussChange = (value: string) => {
    handleFieldChange('codigoTuss', value);
    if (codigoTussError) {
      setCodigoTussError('');
    }
  };

  const handleCodigoTussBlur = async () => {
    const { codigoTuss, tipoItem } = formValuesRef.current;
    const codigo = codigoTuss.trim();
    const tipoProdMed = tipoItem.trim();

    if (!codigo || !tipoProdMed || codigo === codigoTussOriginalRef.current) return;

    setCarregandoDescricao(true);

    try {
      const descricao = await obterDescricaoItem({ codigo, tipoProdMed });
      if (descricao) {
        setFormValues((current) => {
          const updated = { ...current, descricao };
          formValuesRef.current = updated;
          return updated;
        });
        setCodigoTussError('');
      } else {
        setFormValues((current) => {
          const updated = { ...current, descricao: '' };
          formValuesRef.current = updated;
          return updated;
        });
        setCodigoTussError(CODIGO_TUSS_INVALIDO_MESSAGE);
      }
    } finally {
      setCarregandoDescricao(false);
    }
  };

  const handleSalvar = () => {
    if (!procedimento || codigoTussError) return;
    onSave(procedimento.idTranscricaoItem, formValues);
    onClose();
  };

  return (
    <>
      <ModalDefault
        open={open}
        onClose={onClose}
        title="Editar"
        sxTitle={sxModalTitle}
        maxWidth={696}
      >
        <Box
        component="form"
        sx={sxFormGrid}
        style={{
          ...accessibility.textStyleAccessibility,
          ...accessibility.contrastBorderAccessibility,
        }}
        onSubmit={(event) => {
          event.preventDefault();
          handleSalvar();
        }}
      >
        <Box sx={sxFormRow}>
          <Box sx={sxField}>
            <TypographyAccessibility component="label" sx={sxLabel}>
              Tipo do Item
            </TypographyAccessibility>
            <Select
              value={formValues.tipoItem}
              onChange={(event) =>
                handleFieldChange('tipoItem', event.target.value as TipoItemCodigo | '')
              }
              displayEmpty
              fullWidth
              sx={sxSelect}
              style={accessibility.contrastStyle}
            >
              <MenuItem value="">
                Selecione
              </MenuItem>
              {TIPO_ITEM_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={sxField}>
            <TypographyAccessibility component="label" sx={sxLabel}>
              Código TUSS
            </TypographyAccessibility>
            <TextField
              value={formValues.codigoTuss}
              onChange={(event) => handleCodigoTussChange(event.target.value)}
              onBlur={handleCodigoTussBlur}
              variant="outlined"
              fullWidth
              disabled={carregandoDescricao}
              error={!!codigoTussError}
              sx={sxTextField}
              InputProps={{
                style: { ...accessibility.contrastStyle },
                endAdornment: carregandoDescricao ? (
                  <InputAdornment position="end">
                    <CircularProgress size={20} sx={{ zIndex: '1' }} />
                  </InputAdornment>
                ) : null,
              }}
            />
            {codigoTussError && (
              <Typography tabIndex={0} sx={sxError}>
                {codigoTussError}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={sxField}>
          <TypographyAccessibility component="label" sx={sxLabel}>
            Descrição
          </TypographyAccessibility>
          <TextField
            value={formValues.descricao}
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
            disabled
            inputProps={{ maxLength: DESCRICAO_MAX_LENGTH }}
            sx={sxDescricaoTextField}
            InputProps={{
              style: { ...accessibility.contrastStyle },
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption" color="textSecondary">
                    {formValues.descricao.length} / {DESCRICAO_MAX_LENGTH}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={sxField}>
          <TypographyAccessibility component="label" sx={sxLabel}>
            Quantidade
          </TypographyAccessibility>
          <TextField
            value={formValues.quantidade}
            onChange={(event) => handleFieldChange('quantidade', event.target.value)}
            variant="outlined"
            fullWidth
            sx={sxTextField}
            InputProps={{ style: { ...accessibility.contrastStyle } }}
          />
        </Box>

        <Box sx={sxActions}>
          <DefaultButton variant="outlined" text="VOLTAR" onClick={onClose} />
          <DefaultButton text="SALVAR" type="submit" disabled={!!codigoTussError} />
        </Box>
      </Box>
      </ModalDefault>
    </>
  );
};

export default EditarProcedimentoModal;
